import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import type { TrialEmailType } from '@signalvote/shared';
import { queryKeys } from '@/lib/queryKeys';
import { useCampaignStore } from '@/stores';

const api = getApiClient();

/**
 * Fetch billing configuration (publishable key + price details).
 * Subsidiary-scoped, not campaign-scoped.
 */
export function useBillingConfig() {
  return useQuery({
    queryKey: queryKeys.billing.config(),
    queryFn: () => api.billing.getConfig(),
  });
}

/**
 * Create or retrieve a Stripe subscription.
 * Returns clientSecret for Stripe Elements confirmation.
 */
export function useSubscribe() {
  return useMutation({
    mutationFn: () => api.billing.subscribe(),
  });
}

/**
 * Create a Stripe Customer Portal session and redirect to it.
 * Used by PastDue banner to let users update payment method.
 */
export function useCreatePortal() {
  return useMutation({
    mutationFn: (params: { returnUrl?: string }) =>
      api.billing.createPortal({ returnUrl: params.returnUrl ?? null }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

/**
 * Fetch current subscription status for the selected campaign.
 * Supports polling via refetchInterval option (used by post-payment complete page).
 */
export function useBillingStatus(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: queryKeys.billing.status(),
    queryFn: () => api.billing.getStatus(),
    refetchInterval: options?.refetchInterval,
    refetchOnWindowFocus: true, // Override global false — billing status must sync on tab focus
  });
}

/**
 * Toggle paywall on/off (platform admin only).
 * Updates Zustand store with new subscription status on success.
 */
export function useTogglePaywall() {
  const selectCampaign = useCampaignStore((s) => s.selectCampaign);
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);

  return useMutation({
    mutationFn: (enabled: boolean) => api.billing.togglePaywall({ enabled }),
    onSuccess: (data) => {
      if (selectedCampaign) {
        selectCampaign({
          ...selectedCampaign,
          subscriptionStatus: data.subscriptionStatus,
        });
      }
    },
  });
}

/**
 * Fetch the beta loyalty coupon attached to the selected campaign (platform admin only).
 * Returns null when no coupon is attached (204 from API).
 */
export function useBetaPricing() {
  return useQuery({
    queryKey: queryKeys.billing.betaPricing(),
    queryFn: () => api.betaPricing.get(),
  });
}

/**
 * Attach a Stripe Coupon ID as beta loyalty pricing (platform admin only).
 * On success, invalidates billing config so the subscribe page picks up the discount.
 */
export function useAttachBetaPricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (couponId: string) => api.betaPricing.put({ couponId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.betaPricing() });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.config() });
    },
  });
}

/**
 * Remove beta loyalty pricing from the selected campaign (platform admin only).
 * Backend returns 409 if the campaign has an active subscription.
 */
export function useRemoveBetaPricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.betaPricing.delete(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.betaPricing() });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.config() });
    },
  });
}

/**
 * EML-007: Fetch trial email audit (sent-at timestamps + formatted trial end) for the
 * selected campaign. Backend returns 403 for non-platform-admins — callers MUST pass
 * `enabled: false` when the user is not an admin (and when no campaign is selected) so
 * the hook does not fire a 403-producing request on every admin settings page load.
 */
export function useTrialEmailAudit(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.billing.trialEmails(),
    queryFn: () => api.billing.getTrialEmails(),
    enabled: options?.enabled ?? true,
  });
}

/**
 * EML-007: Admin-override resend of a specific trial email (T3d / T1d / T1dAfter).
 * Server bypasses every scheduled-job skip condition. Response is the updated audit DTO;
 * `setQueryData` writes it straight into the cache so the UI reflects the new timestamp
 * without a second GET.
 */
export function useSendTrialEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (type: TrialEmailType) => api.billing.sendTrialEmail({ type }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.billing.trialEmails(), data);
    },
  });
}
